import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MapPin, Users, Gift, TrendingUp, DollarSign, Building,
  Plus, ChevronRight, CheckCircle, AlertCircle, Clock, Target,
  BarChart3, Globe, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminExpansion = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddCity, setShowAddCity] = useState(false);
  const [newCity, setNewCity] = useState({
    name: '',
    slug: '',
    province_state: '',
    population: 0,
    avg_home_value: 0,
    realtor_count: 0,
    renovation_demand_score: 5
  });

  useEffect(() => {
    fetchExpansionData();
  }, [token]);

  const fetchExpansionData = async () => {
    try {
      const response = await axios.get(`${API}/admin/expansion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching expansion data:', error);
      toast.error('Failed to load expansion data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCity = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cities`, newCity, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('City added successfully!');
      setShowAddCity(false);
      setNewCity({ name: '', slug: '', province_state: '', population: 0, avg_home_value: 0, realtor_count: 0, renovation_demand_score: 5 });
      fetchExpansionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add city');
    }
  };

  const qualifyCity = async (slug) => {
    try {
      const response = await axios.get(`${API}/cities/${slug}/qualify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = response.data;
      
      if (result.ready_to_launch) {
        toast.success(`${result.city} is ready to launch! 🚀`);
      } else {
        const missing = [];
        if (!result.sponsors_ready) missing.push(`${result.requirements.sponsors_minimum - result.sponsors_count} more sponsors`);
        if (!result.contractors_ready) missing.push(`${result.requirements.contractors_minimum - result.contractors_count} more contractors`);
        if (!result.giveaways_ready) missing.push(`${result.requirements.giveaways_minimum - result.giveaways_count} more giveaways`);
        toast.info(`${result.city} needs: ${missing.join(', ')}`);
      }
    } catch (error) {
      toast.error('Failed to check qualification');
    }
  };

  const launchCity = async (slug) => {
    try {
      await axios.put(`${API}/cities/${slug}`, { status: 'active' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('City launched! 🎉');
      fetchExpansionData();
    } catch (error) {
      toast.error('Failed to launch city');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'launching': return 'bg-amber-100 text-amber-700';
      case 'qualifying': return 'bg-blue-100 text-blue-700';
      default: return 'bg-neutral-100 text-neutral-600';
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-expansion">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
            Expansion Dashboard
          </h1>
          <p className="text-neutral-600 mt-1">Manage city-by-city rollout</p>
        </div>
        <Button onClick={() => setShowAddCity(true)} className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl" data-testid="add-city-btn">
          <Plus className="w-5 h-5 mr-2" />
          Add City
        </Button>
      </div>

      {/* Overview Stats */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-neutral-500">Active Cities</span>
            </div>
            <p className="text-3xl font-bold text-neutral-900">{data.cities.active}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-neutral-500">Total Sponsors</span>
            </div>
            <p className="text-3xl font-bold text-neutral-900">{data.totals.sponsors}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-neutral-500">Total Leads</span>
            </div>
            <p className="text-3xl font-bold text-neutral-900">{data.totals.leads}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-neutral-200"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF3B30]/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <span className="text-neutral-500">Total Revenue</span>
            </div>
            <p className="text-3xl font-bold text-neutral-900">${data.totals.revenue.toLocaleString()}</p>
          </motion.div>
        </div>
      )}

      {/* CRM Pipeline */}
      {data && (
        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Realtor Acquisition Pipeline</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <p className="text-3xl font-bold text-blue-600">{data.crm_pipeline.new}</p>
              <p className="text-sm text-blue-600">New Leads</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-xl">
              <p className="text-3xl font-bold text-amber-600">{data.crm_pipeline.contacted}</p>
              <p className="text-sm text-amber-600">Contacted</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <p className="text-3xl font-bold text-purple-600">{data.crm_pipeline.demo_scheduled}</p>
              <p className="text-sm text-purple-600">Demo Scheduled</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <p className="text-3xl font-bold text-green-600">{data.crm_pipeline.closed_won}</p>
              <p className="text-sm text-green-600">Closed Won</p>
            </div>
          </div>
        </div>
      )}

      {/* Cities Table */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        <div className="p-6 border-b border-neutral-200">
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Cities</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-medium text-neutral-500">City</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-neutral-500">Status</th>
                <th className="text-center px-6 py-3 text-sm font-medium text-neutral-500">Sponsors</th>
                <th className="text-center px-6 py-3 text-sm font-medium text-neutral-500">Contractors</th>
                <th className="text-center px-6 py-3 text-sm font-medium text-neutral-500">Giveaways</th>
                <th className="text-center px-6 py-3 text-sm font-medium text-neutral-500">Leads</th>
                <th className="text-right px-6 py-3 text-sm font-medium text-neutral-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {data?.cities.list.map((city) => (
                <tr key={city.city_id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-neutral-500" />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">{city.name}</p>
                        <p className="text-sm text-neutral-500">{city.province_state}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(city.status)}`}>
                      {city.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center font-medium">{city.sponsors_count}</td>
                  <td className="px-6 py-4 text-center font-medium">{city.contractors_count}</td>
                  <td className="px-6 py-4 text-center font-medium">{city.giveaways_count}</td>
                  <td className="px-6 py-4 text-center font-medium">{city.total_leads}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => qualifyCity(city.slug)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Target className="w-4 h-4" />
                      </Button>
                      {city.status === 'qualifying' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => launchCity(city.slug)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Zap className="w-4 h-4" />
                        </Button>
                      )}
                      <Link to={`/city/${city.slug}`}>
                        <Button variant="ghost" size="sm">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add City Modal */}
      {showAddCity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
          >
            <h2 className="text-xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Add New City
            </h2>
            
            <form onSubmit={handleAddCity} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>City Name</Label>
                  <Input
                    value={newCity.name}
                    onChange={(e) => setNewCity({ ...newCity, name: e.target.value })}
                    placeholder="Toronto"
                    className="mt-1 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input
                    value={newCity.slug}
                    onChange={(e) => setNewCity({ ...newCity, slug: e.target.value.toLowerCase() })}
                    placeholder="toronto"
                    className="mt-1 rounded-xl"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Province/State</Label>
                <Input
                  value={newCity.province_state}
                  onChange={(e) => setNewCity({ ...newCity, province_state: e.target.value })}
                  placeholder="Ontario"
                  className="mt-1 rounded-xl"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Population</Label>
                  <Input
                    type="number"
                    value={newCity.population}
                    onChange={(e) => setNewCity({ ...newCity, population: parseInt(e.target.value) || 0 })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Avg Home Value ($)</Label>
                  <Input
                    type="number"
                    value={newCity.avg_home_value}
                    onChange={(e) => setNewCity({ ...newCity, avg_home_value: parseFloat(e.target.value) || 0 })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Realtor Count (est.)</Label>
                  <Input
                    type="number"
                    value={newCity.realtor_count}
                    onChange={(e) => setNewCity({ ...newCity, realtor_count: parseInt(e.target.value) || 0 })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Demand Score (1-10)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={newCity.renovation_demand_score}
                    onChange={(e) => setNewCity({ ...newCity, renovation_demand_score: parseFloat(e.target.value) || 5 })}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddCity(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl"
                >
                  Add City
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminExpansion;
